/**
 * Coordinates asynchronous Vault archive jobs and browser delivery.
 *
 * Multi-item downloads are built server-side after execution-time permission
 * checks. The hook exposes progress and obtains the final short-lived URL
 * without treating job submission as permission to download.
 */
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import type { ItemResponse } from '../types'
import { getVaultDownload, startVaultDownload } from '../services/vault-api'
import { waitForVaultJob } from '../services/vault-jobs'
import { triggerBrowserDownload } from '../services/browser-download'

interface VaultDownloadsOptions {
  api: NodeApiClient
  orgId: string
  vaultId: string
  items: ItemResponse[]
  scope: string
}

export function pruneVaultSelection(
  selectedIds: Set<string>,
  items: ItemResponse[],
): Set<string> {
  const visible = new Set(items.map(({ item }) => item.itemId))
  const retained = [...selectedIds].filter(itemId => visible.has(itemId))
  // cm-coding: React compares state by identity. Preserve the current Set
  // when pruning changes nothing so a caller-created items array cannot turn
  // this synchronization effect into an endless state-update loop.
  return retained.length === selectedIds.size ? selectedIds : new Set(retained)
}

export function useVaultDownloads({
  api,
  orgId,
  vaultId,
  items,
  scope,
}: VaultDownloadsOptions) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [preparing, setPreparing] = useState(false)

  useEffect(() => setSelectedIds(new Set()), [scope])
  useEffect(() => {
    // Intent: selection is scoped to the rendered dataset. A delete or refresh
    // must not leave an invisible object queued for ZIP download.
    setSelectedIds(current => pruneVaultSelection(current, items))
  }, [items])

  const setSelected = (itemId: string, selected: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current)
      if (selected) next.add(itemId)
      else next.delete(itemId)
      return next
    })
  }

  const selectAll = (selected: boolean) => {
    setSelectedIds(selected ? new Set(items.map(({ item }) => item.itemId)) : new Set())
  }

  const download = async () => {
    if (!selectedIds.size || preparing) return
    setPreparing(true)
    const notification = toast.loading('Preparing secure ZIP…')
    try {
      const { jobId } = await startVaultDownload(
        api,
        orgId,
        vaultId,
        [...selectedIds],
      )
      await waitForVaultJob(api, orgId, vaultId, jobId)
      const url = await getVaultDownload(api, orgId, vaultId, jobId)
      triggerBrowserDownload(url, 'vault-download.zip')
      setSelectedIds(new Set())
      toast.success('ZIP download ready', { id: notification })
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? error?.message ?? 'ZIP preparation failed', {
        id: notification,
      })
    } finally {
      setPreparing(false)
    }
  }

  return { selectedIds, preparing, setSelected, selectAll, download }
}
